// Local grading — mirrors how iOS grades each type before posting to /sessions/
// attempt. Objective types are graded here; self-graded types (flashcard, short
// answer, and any unknown type) ask the learner to mark themselves.
import {
  MCQ_FAMILY_TYPES,
  type DefinitionMatchContent,
  type ErrorIdentificationContent,
  type ImageLabelingContent,
  type FillBlankContent,
  type FillBlankSlot,
  type ClozePassageContent,
  type CrosswordContent,
  type DerivationContent,
  type EquationBuilderContent,
  type InflectionTableContent,
  type MultipleChoiceContent,
  type OrderingContent,
  type ReadingComprehensionContent,
  type TrueFalseContent,
  type VocabPairContent,
  type VocabPairPoolContent,
  type ArticleDrillBatchContent,
  type RedemittelLabelContent,
  type ConnectorClassifyContent,
  type TransformationPairContent,
  type TypedRecallFillContent,
  type TypedBlank,
  type SentenceReorderClausesContent,
  type HauptaussagenContent,
  type ParagraphGapSelectContent,
  type LesenStatementCompareContent,
  type LesenGuidedPassageContent,
} from "./content-types";
import { normaliseLetterGrid } from "./crossword-norm";

/** Typed-recall grade mirroring iOS TypedRecallGrader: trim + strip edge
 *  punctuation, optional lowercase + umlaut fold, collapse whitespace, then
 *  exact-or-within-typo-distance match against primary + alternates. Returns
 *  true when correct (incl. an accepted typo). */
function typedNormalize(s: string, casing: string, umlaut: boolean): string {
  let out = (s ?? "").trim().replace(/^[\p{P}\s]+|[\p{P}\s]+$/gu, "");
  if (casing !== "strict") out = out.toLowerCase();
  if (umlaut) {
    out = out
      .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue")
      .replace(/Ä/g, "Ae").replace(/Ö/g, "Oe").replace(/Ü/g, "Ue")
      .replace(/ß/g, "ss");
  }
  return out.split(/\s+/).filter(Boolean).join(" ");
}

export function typedRecallCorrect(
  input: string,
  primary: string,
  alternates: string[],
  casing: string,
  umlaut: boolean,
  maxTypo: number,
): boolean {
  const got = typedNormalize(input, casing, umlaut);
  if (!got) return false;
  const candidates = [primary, ...(alternates ?? [])];
  let best = Infinity;
  let bestTarget = "";
  for (const c of candidates) {
    const t = typedNormalize(c, casing, umlaut);
    if (!t) continue;
    if (got === t) return true;
    const d = levenshtein(got, t);
    if (d < best) {
      best = d;
      bestTarget = t;
    }
  }
  if (maxTypo > 0 && bestTarget) {
    const allowed = Math.min(maxTypo, Math.max(1, Math.floor(bestTarget.length / 5)));
    if (best <= allowed && Math.max(bestTarget.length, 5) >= 5) return true;
  }
  return false;
}

function blankCorrect(b: TypedBlank, val: string): boolean {
  return typedRecallCorrect(
    val ?? "", b.accepted_primary, b.accepted_alts ?? [],
    b.casing, b.umlaut_tolerance, b.max_typo_distance,
  );
}

export function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Aggressive normalisation for fuzzy comparison: lowercases, unwraps LaTeX
 *  (`\text{dx}`→`dx`, `\lambda`→`lambda`), and strips delimiters/braces/spaces/
 *  punctuation so "$\lambda$" ≈ "lambda" and "δx" ≈ "deltax". */
export function normLoose(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/\\(?:text|mathrm|mathbf|mathit|operatorname)\{([^}]*)\}/gi, "$1")
    .replace(/\\([a-z]+)/gi, "$1")
    .replace(/[^a-z0-9]+/g, "");
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
}

/** Normalise an inflected form for tolerant comparison: trim + lowercase, fold
 *  German umlauts/ß (ä→ae, ö→oe, ü→ue, ß→ss), then strip any remaining combining
 *  accents (é→e). */
export function normalizeForm(s: string): string {
  return (s ?? "")
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

/** Whether a typed inflection-table entry matches the expected answer: exact
 *  under {@link normalizeForm}, or within Levenshtein distance 1. */
export function cellMatches(expected: string, val: string): boolean {
  const want = normalizeForm(expected);
  const got = normalizeForm(val);
  if (!got) return false;
  if (got === want) return true;
  return levenshtein(got, want) <= 1;
}

/** Whether a fill-blank answer is acceptable. Word-bank picks (the blank has
 *  distractors → the learner chose from chips) require an exact normalized
 *  match; typed answers get LaTeX-aware fuzzy tolerance (small typos OK). */
export function blankMatches(blank: FillBlankSlot, val: string): boolean {
  const accepted = [blank.correct, ...(blank.accepted_alternatives ?? [])];
  if (blank.distractors && blank.distractors.length > 0) {
    return accepted.map(norm).includes(norm(val ?? ""));
  }
  const got = normLoose(val ?? "");
  if (!got) return false;
  return accepted.some((a) => {
    const t = normLoose(a);
    if (!t) return false;
    if (got === t) return true;
    const thr = Math.max(1, Math.ceil(Math.max(t.length, got.length) / 5));
    return levenshtein(got, t) <= thr;
  });
}

function setEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

const SELF_GRADED = new Set<string>(["flashcard", "short_answer"]);

/** Renderers we have first-class support for. Anything else → unknown fallback. */
export const SUPPORTED_TYPES = new Set<string>([
  "multiple_choice",
  "true_false",
  "fill_blank",
  "cloze_passage",
  "flashcard",
  "short_answer",
  "ordering",
  "definition_match",
  "vocab_pair",
  "inflection_table",
  "reading_comprehension",
  "error_identification",
  "equation_builder",
  "derivation",
  "image_labeling",
  "crossword",
  "vocab_pair_pool",
  "article_drill_batch",
  "redemittel_label",
  "connector_classify",
  "transformation_pair",
  "typed_recall_fill",
  "sentence_reorder_clauses",
  "hauptaussagen_passage",
  "paragraph_gap_select",
  "lesen_statement_compare_passage",
  "lesen_guided_passage",
  "socratic_chain",
  ...MCQ_FAMILY_TYPES,
]);

export function isSupported(type: string): boolean {
  return SUPPORTED_TYPES.has(type);
}

/** Self-graded types (incl. unknown) reveal the answer and let the learner mark
 *  whether they got it. There's no objective key to grade against. */
export function isSelfGraded(type: string): boolean {
  return SELF_GRADED.has(type) || !isSupported(type);
}

/** Whether the current answer is complete enough to submit (objective types). */
export function hasAnswer(type: string, content: Record<string, unknown>, answer: unknown): boolean {
  if (isSelfGraded(type)) return true;
  switch (type) {
    case "true_false":
      return typeof answer === "boolean";
    case "multiple_choice": {
      const mc = content as unknown as MultipleChoiceContent;
      if (mc.correct_option_ids && mc.correct_option_ids.length >= 2) {
        return Array.isArray(answer) && answer.length > 0;
      }
      return typeof answer === "string" && !!answer;
    }
    case "fill_blank":
    case "cloze_passage": {
      const blanks = (content as unknown as FillBlankContent).blanks ?? [];
      const arr = (answer as string[]) ?? [];
      return blanks.length > 0 && blanks.every((_, i) => (arr[i] ?? "").trim() !== "");
    }
    case "ordering":
      return Array.isArray(answer) && answer.length > 0;
    case "definition_match": {
      const pairs = (content as unknown as DefinitionMatchContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return pairs.length > 0 && pairs.every((p) => !!map[p.term_id]);
    }
    case "vocab_pair": {
      const pairs = (content as unknown as VocabPairContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return pairs.length > 0 && pairs.every((p) => !!map[p.pair_id]);
    }
    case "inflection_table": {
      const cells = (content as unknown as InflectionTableContent).cells ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return cells.length > 0 && cells.every((cell) => (map[cell.cell_id] ?? "").trim() !== "");
    }
    case "reading_comprehension": {
      const subs = (content as unknown as ReadingComprehensionContent).sub_questions ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return subs.length > 0 && subs.every((s) => !!map[s.id]);
    }
    case "error_identification":
      return typeof answer === "string" && !!answer;
    case "crossword": {
      const entries = (content as unknown as CrosswordContent).entries ?? [];
      const map = (answer as Record<string, string>) ?? {};
      // Every entry must be filled to its answer length (all-or-nothing).
      return (
        entries.length > 0 &&
        entries.every((e) => normaliseLetterGrid(map[e.id] ?? "").length === e.answer.length)
      );
    }
    case "vocab_pair_pool": {
      const pairs = (content as unknown as VocabPairPoolContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return pairs.length > 0 && pairs.every((p) => !!map[p.pair_id]);
    }
    case "article_drill_batch": {
      const items = (content as unknown as ArticleDrillBatchContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return items.length > 0 && items.every((it) => !!map[it.id]);
    }
    case "redemittel_label": {
      const items = (content as unknown as RedemittelLabelContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return items.length > 0 && items.every((it) => !!map[it.id]);
    }
    case "connector_classify": {
      const items = (content as unknown as ConnectorClassifyContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return items.length > 0 && items.every((it) => !!map[it.id]);
    }
    case "transformation_pair":
      return typeof answer === "string" && answer.trim() !== "";
    case "typed_recall_fill": {
      const blanks = (content as unknown as TypedRecallFillContent).blanks ?? [];
      const arr = (answer as string[]) ?? [];
      return blanks.length > 0 && blanks.every((_, i) => (arr[i] ?? "").trim() !== "");
    }
    case "sentence_reorder_clauses": {
      const clauses = (content as unknown as SentenceReorderClausesContent).clauses ?? [];
      const map = (answer as Record<string, string[]>) ?? {};
      return clauses.length > 0 &&
        clauses.every((cl, i) => (map[String(i)] ?? []).length === cl.correct_order.length);
    }
    case "hauptaussagen_passage":
      return Array.isArray(answer);
    case "paragraph_gap_select": {
      const gaps = (content as unknown as ParagraphGapSelectContent).gaps ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return gaps.length > 0 && gaps.every((g) => !!map[String(g.index)]);
    }
    case "lesen_statement_compare_passage": {
      const stmts = (content as unknown as LesenStatementCompareContent).statements ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return stmts.length > 0 && stmts.every((s) => !!map[s.id]);
    }
    case "lesen_guided_passage": {
      const c = content as unknown as LesenGuidedPassageContent;
      const a = (answer as { gaps?: Record<string, string> }) ?? {};
      const gmap = a.gaps ?? {};
      const gaps = c.passage?.gaps ?? [];
      return gaps.length > 0 && gaps.every((g) => !!gmap[String(g.index)]);
    }
    case "equation_builder": {
      const blankCount = ((content as unknown as EquationBuilderContent).equation_template ?? "")
        .split("___").length - 1;
      const arr = (answer as string[]) ?? [];
      return blankCount > 0 && Array.from({ length: blankCount }).every((_, i) => !!(arr[i] ?? "").trim());
    }
    case "derivation":
      return Array.isArray(answer) && answer.length > 0;
    case "image_labeling": {
      const markers = (content as unknown as ImageLabelingContent).markers ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return markers.length > 0 && markers.every((m) => !!map[String(m.id)]);
    }
    case "socratic_chain":
      // The renderer's branching walk sets a boolean once a terminal is reached.
      return typeof answer === "boolean";
    default:
      // MCQ family → single select
      return typeof answer === "string" && !!answer;
  }
}

/** Grade an objective answer. Self-graded types must not be passed here. */
export function grade(type: string, content: Record<string, unknown>, answer: unknown): boolean {
  switch (type) {
    case "true_false":
      return answer === (content as unknown as TrueFalseContent).correct;
    case "multiple_choice": {
      const mc = content as unknown as MultipleChoiceContent;
      if (mc.correct_option_ids && mc.correct_option_ids.length >= 2) {
        return Array.isArray(answer) && setEqual(answer as string[], mc.correct_option_ids);
      }
      return answer === mc.correct_option_id;
    }
    case "fill_blank":
    case "cloze_passage": {
      const blanks = (content as unknown as FillBlankContent | ClozePassageContent).blanks ?? [];
      const arr = (answer as string[]) ?? [];
      const sorted = [...blanks].sort((a, b) => a.index - b.index);
      return sorted.every((blank, i) => blankMatches(blank, arr[i] ?? ""));
    }
    case "ordering": {
      const steps = (content as unknown as OrderingContent).steps ?? [];
      const target = [...steps].sort((a, b) => a.index - b.index).map((s) => s.token_id);
      const got = (answer as string[]) ?? [];
      return got.length === target.length && got.every((id, i) => id === target[i]);
    }
    case "definition_match": {
      const pairs = (content as unknown as DefinitionMatchContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      // answer maps term_id → chosen definition text; correct when each maps to its own.
      return pairs.every((p) => map[p.term_id] === p.definition);
    }
    case "vocab_pair": {
      const pairs = (content as unknown as VocabPairContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return pairs.every((p) => map[p.pair_id] === p.en);
    }
    case "inflection_table": {
      const cells = (content as unknown as InflectionTableContent).cells ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return cells.every((cell) => cellMatches(cell.answer, map[cell.cell_id] ?? ""));
    }
    case "reading_comprehension": {
      const subs = (content as unknown as ReadingComprehensionContent).sub_questions ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return subs.every((s) => {
        const chosen = s.options.find((o) => o.id === map[s.id]);
        return !!chosen?.is_correct;
      });
    }
    case "error_identification": {
      const tokens = (content as unknown as ErrorIdentificationContent).sentence_tokens ?? [];
      return !!tokens.find((t) => t.id === answer)?.is_error;
    }
    case "crossword": {
      const entries = (content as unknown as CrosswordContent).entries ?? [];
      const map = (answer as Record<string, string>) ?? {};
      // All-or-nothing: every entry's folded answer matches (mirrors iOS).
      return entries.every((e) => normaliseLetterGrid(map[e.id] ?? "") === e.answer);
    }
    case "vocab_pair_pool": {
      const pairs = (content as unknown as VocabPairPoolContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return pairs.every((p) => map[p.pair_id] === p.en);
    }
    case "article_drill_batch": {
      const items = (content as unknown as ArticleDrillBatchContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return items.every((it) => map[it.id] === it.correct_article);
    }
    case "redemittel_label": {
      const items = (content as unknown as RedemittelLabelContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return items.every((it) => map[it.id] === it.function_label);
    }
    case "connector_classify": {
      const items = (content as unknown as ConnectorClassifyContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return items.every((it) => map[it.id] === it.correct_class);
    }
    case "transformation_pair": {
      const c = content as unknown as TransformationPairContent;
      if (c.render_mode === "typed") {
        return typedRecallCorrect(
          String(answer ?? ""), c.target_primary, c.target_accepted ?? [],
          "ignore", true, 1,
        );
      }
      return !!(c.variants ?? []).find((v) => v.text === answer)?.is_correct;
    }
    case "typed_recall_fill": {
      const blanks = (content as unknown as TypedRecallFillContent).blanks ?? [];
      const arr = (answer as string[]) ?? [];
      return blanks.length > 0 && blanks.every((b, i) => blankCorrect(b, arr[i] ?? ""));
    }
    case "sentence_reorder_clauses": {
      const clauses = (content as unknown as SentenceReorderClausesContent).clauses ?? [];
      const map = (answer as Record<string, string[]>) ?? {};
      return clauses.every((cl, i) => {
        const got = map[String(i)] ?? [];
        return got.length === cl.correct_order.length &&
          got.every((id, j) => id === cl.correct_order[j]);
      });
    }
    case "hauptaussagen_passage": {
      const cands = (content as unknown as HauptaussagenContent).candidates ?? [];
      const picked = new Set((answer as string[]) ?? []);
      return cands.every((c) => picked.has(c.id) === c.is_main);
    }
    case "paragraph_gap_select": {
      const gaps = (content as unknown as ParagraphGapSelectContent).gaps ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return gaps.every((g) => map[String(g.index)] === g.correct_label);
    }
    case "lesen_statement_compare_passage": {
      const stmts = (content as unknown as LesenStatementCompareContent).statements ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return stmts.every((s) => map[s.id] === s.verdict);
    }
    case "lesen_guided_passage": {
      const c = content as unknown as LesenGuidedPassageContent;
      const a = (answer as { gaps?: Record<string, string>; stmts?: string[] }) ?? {};
      const gmap = a.gaps ?? {};
      const picked = new Set(a.stmts ?? []);
      const gapsOk = (c.passage?.gaps ?? []).every((g) => gmap[String(g.index)] === g.correct_label);
      const stmtsOk = (c.comprehension?.statements ?? []).every(
        (s) => (s.verdict === "discussed") === picked.has(s.id),
      );
      return gapsOk && stmtsOk;
    }
    case "equation_builder": {
      const eb = content as unknown as EquationBuilderContent;
      const blankCount = (eb.equation_template ?? "").split("___").length - 1;
      const arr = (answer as string[]) ?? [];
      const correct = eb.correct ?? [];
      // All-or-nothing: every blank's pick equals correct[i] (trim + case-insensitive).
      return (
        blankCount > 0 &&
        correct.length === blankCount &&
        correct.every((want, i) => norm(arr[i] ?? "") === norm(want))
      );
    }
    case "derivation": {
      const der = content as unknown as DerivationContent;
      // Content-based: picked step TEXTS must equal the canonical order; any
      // distractor token makes it wrong. Mirrors iOS DerivationView grading.
      const picked = (answer as string[]) ?? [];
      if (picked.some((tk) => tk.startsWith("distractor-"))) return false;
      const byToken = new Map<string, string>();
      for (const s of der.steps ?? []) byToken.set(`correct-${s.index}`, s.text);
      const target = [...(der.steps ?? [])].sort((a, b) => a.index - b.index).map((s) => norm(s.text));
      const got = picked.map((tk) => norm(byToken.get(tk) ?? ""));
      return got.length === target.length && got.every((txt, i) => txt === target[i]);
    }
    case "image_labeling": {
      const markers = (content as unknown as ImageLabelingContent).markers ?? [];
      const map = (answer as Record<string, string>) ?? {};
      // All-or-nothing: every marker's assigned label matches its correct label.
      return markers.length > 0 && markers.every((m) => norm(map[String(m.id)] ?? "") === norm(m.correct));
    }
    case "socratic_chain":
      // true = the walk ended on a terminal node answered correctly (mirrors iOS).
      return answer === true;
    default:
      // MCQ family → single select against correct_option_id
      return answer === (content as { correct_option_id?: string }).correct_option_id;
  }
}

/** Per-element grade for multi-part types (partial grading). `null` for
 *  single-response types — they stay binary (mirrors iOS). `correctIds`/
 *  `wrongIds` are in display order so the wrong-drill can resume from the first
 *  wrong sub-part. */
export interface PartGrade {
  correctIds: string[];
  wrongIds: string[];
  /** Display order of all element ids (correct ∪ wrong). */
  order: string[];
}

export function gradeParts(
  type: string,
  content: Record<string, unknown>,
  answer: unknown,
): PartGrade | null {
  const split = (els: { id: string; correct: boolean }[]): PartGrade => ({
    correctIds: els.filter((e) => e.correct).map((e) => e.id),
    wrongIds: els.filter((e) => !e.correct).map((e) => e.id),
    order: els.map((e) => e.id),
  });
  switch (type) {
    case "reading_comprehension": {
      const subs = (content as unknown as ReadingComprehensionContent).sub_questions ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(
        subs.map((s) => ({
          id: s.id,
          correct: !!s.options.find((o) => o.id === map[s.id])?.is_correct,
        })),
      );
    }
    case "vocab_pair": {
      const pairs = (content as unknown as VocabPairContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(pairs.map((p) => ({ id: p.pair_id, correct: map[p.pair_id] === p.en })));
    }
    case "inflection_table": {
      const cells = (content as unknown as InflectionTableContent).cells ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(
        cells.map((cell) => ({
          id: cell.cell_id,
          correct: cellMatches(cell.answer, map[cell.cell_id] ?? ""),
        })),
      );
    }
    case "definition_match": {
      const pairs = (content as unknown as DefinitionMatchContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(pairs.map((p) => ({ id: p.term_id, correct: map[p.term_id] === p.definition })));
    }
    case "fill_blank":
    case "cloze_passage": {
      const blanks = (content as unknown as FillBlankContent | ClozePassageContent).blanks ?? [];
      const arr = (answer as string[]) ?? [];
      const sorted = [...blanks].sort((a, b) => a.index - b.index);
      return split(
        sorted.map((blank, i) => ({
          id: String(blank.index),
          correct: blankMatches(blank, arr[i] ?? ""),
        })),
      );
    }
    case "vocab_pair_pool": {
      const pairs = (content as unknown as VocabPairPoolContent).pairs ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(pairs.map((p) => ({ id: p.pair_id, correct: map[p.pair_id] === p.en })));
    }
    case "article_drill_batch": {
      const items = (content as unknown as ArticleDrillBatchContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(items.map((it) => ({ id: it.id, correct: map[it.id] === it.correct_article })));
    }
    case "redemittel_label": {
      const items = (content as unknown as RedemittelLabelContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(items.map((it) => ({ id: it.id, correct: map[it.id] === it.function_label })));
    }
    case "connector_classify": {
      const items = (content as unknown as ConnectorClassifyContent).items ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(items.map((it) => ({ id: it.id, correct: map[it.id] === it.correct_class })));
    }
    case "typed_recall_fill": {
      const blanks = (content as unknown as TypedRecallFillContent).blanks ?? [];
      const arr = (answer as string[]) ?? [];
      return split(blanks.map((b, i) => ({ id: b.id, correct: blankCorrect(b, arr[i] ?? "") })));
    }
    case "sentence_reorder_clauses": {
      const clauses = (content as unknown as SentenceReorderClausesContent).clauses ?? [];
      const map = (answer as Record<string, string[]>) ?? {};
      return split(clauses.map((cl, i) => {
        const got = map[String(i)] ?? [];
        const ok = got.length === cl.correct_order.length &&
          got.every((id, j) => id === cl.correct_order[j]);
        return { id: String(i), correct: ok };
      }));
    }
    case "hauptaussagen_passage": {
      const cands = (content as unknown as HauptaussagenContent).candidates ?? [];
      const picked = new Set((answer as string[]) ?? []);
      return split(cands.map((c) => ({ id: c.id, correct: picked.has(c.id) === c.is_main })));
    }
    case "paragraph_gap_select": {
      const gaps = (content as unknown as ParagraphGapSelectContent).gaps ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(gaps.map((g) => ({ id: String(g.index), correct: map[String(g.index)] === g.correct_label })));
    }
    case "lesen_statement_compare_passage": {
      const stmts = (content as unknown as LesenStatementCompareContent).statements ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(stmts.map((s) => ({ id: s.id, correct: map[s.id] === s.verdict })));
    }
    case "image_labeling": {
      const markers = (content as unknown as ImageLabelingContent).markers ?? [];
      const map = (answer as Record<string, string>) ?? {};
      return split(
        markers.map((m) => ({
          id: String(m.id),
          correct: norm(map[String(m.id)] ?? "") === norm(m.correct),
        })),
      );
    }
    default:
      return null; // single-response → binary
  }
}
